// Excel на „Излишък от продажби": групите по склад такива, каквито са на
// екрана — след търсенето, филтрите Склад/Категория и подредбата (по име или по
// стойност). За всеки склад — продуктите му, после ред „Общо · N продукта" с
// нето и бруто на склада; накрая „Общо непокрит излишък" за всичко изнесено.
// Количеството е с МИНУС, както на екрана (това е липса, не излишък — в
// данните е положително, `shortageShared`); мерната единица е в своя колона.
import type { TFunction } from "i18next";
import type { ExcelColumn } from "../../../../utils/excelExport";
import { excelMoney } from "../../components/list/excelCells";
import { formatDate, type ShortageWarehouseGroup } from "./shortageShared";

export type ShortageExcelRow = {
  warehouse: string;
  product: string;
  category: string;
  unit: string;
  // null в редовете „Общо"
  quantity: number | null;
  lastPrice: number | null;
  net: number;
  gross: number;
  reports: number | null;
  oldestReport: string;
};

export type ShortageExcelLabels = {
  // „Общо · 3 продукта" (складът) / „12 продукта" (всички)
  groupTotal: (count: number) => string;
  productsCount: (count: number) => string;
  total: string;
};

export const shortageExcelRows = (
  groups: ShortageWarehouseGroup[],
  labels: ShortageExcelLabels,
): ShortageExcelRow[] => {
  const rows: ShortageExcelRow[] = [];
  let count = 0;
  let net = 0;
  let gross = 0;
  for (const group of groups) {
    for (const row of group.rows) {
      rows.push({
        warehouse: group.warehouseName,
        product: row.productName,
        category: row.category ?? "",
        unit: row.unit ?? "",
        // -0 би излязло като „-0" в Excel
        quantity: row.quantity ? -row.quantity : 0,
        lastPrice: row.lastDeliveryPrice,
        net: excelMoney(row.quantityNet),
        gross: excelMoney(row.quantityGross),
        reports: row.breakdown.length || null,
        oldestReport: row.earliestReportDate ? formatDate(row.earliestReportDate) : "",
      });
    }
    rows.push({
      warehouse: group.warehouseName,
      product: labels.groupTotal(group.rows.length),
      category: "",
      unit: "",
      quantity: null,
      lastPrice: null,
      net: excelMoney(group.net),
      gross: excelMoney(group.gross),
      reports: null,
      oldestReport: "",
    });
    count += group.rows.length;
    net += group.net;
    gross += group.gross;
  }
  if (!count) return rows;
  rows.push({
    warehouse: labels.total,
    product: labels.productsCount(count),
    category: "",
    unit: "",
    quantity: null,
    lastPrice: null,
    net: excelMoney(net),
    gross: excelMoney(gross),
    reports: null,
    oldestReport: "",
  });
  return rows;
};

export const shortageExcelLabels = (t: TFunction): ShortageExcelLabels => ({
  groupTotal: (count) => `${t("inventoryPage.total")} · ${t("inventoryPage.shortageProductsCount", { count })}`,
  productsCount: (count) => t("inventoryPage.shortageProductsCount", { count }),
  total: t("inventoryPage.shortageTotal"),
});

export const shortageExcelColumns = (t: TFunction): ExcelColumn<ShortageExcelRow>[] => [
  { header: t("inventoryPage.colWarehouse"), value: (row) => row.warehouse, width: 22 },
  { header: t("inventoryPage.colProductName"), value: (row) => row.product, width: 30 },
  { header: t("inventoryPage.colCategory"), value: (row) => row.category },
  { header: t("inventoryPage.colUnit"), value: (row) => row.unit },
  { header: t("inventoryPage.colDeficitQty"), value: (row) => row.quantity },
  { header: t("inventoryPage.colLastPrice"), value: (row) => row.lastPrice },
  { header: t("inventoryPage.colNet"), value: (row) => row.net },
  { header: t("inventoryPage.colGross"), value: (row) => row.gross },
  { header: t("inventoryPage.tbColReports"), value: (row) => row.reports },
  { header: t("inventoryPage.tbColOldestReport"), value: (row) => row.oldestReport, width: 14 },
];
